import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export type Theme = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'pmp_theme'

/** Returns the resolved light/dark value for the OS preference. */
function systemPreference(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** Applies the correct data-theme attribute to <html>. */
export function applyTheme(theme: Theme) {
  const resolved = theme === 'system' ? systemPreference() : theme
  document.documentElement.setAttribute('data-theme', resolved)
}

export const useThemeStore = defineStore('theme', () => {
  const saved = (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? 'system'
  const theme = ref<Theme>(saved)

  // Apply immediately on store init (handles FOUC when called before mount)
  applyTheme(theme.value)

  // When the user's explicit choice changes, persist and re-apply
  watch(theme, (t) => {
    applyTheme(t)
    localStorage.setItem(STORAGE_KEY, t)
  })

  // Listen for OS-level preference changes; only re-apply when in 'system' mode
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  mq.addEventListener('change', () => {
    if (theme.value === 'system') applyTheme('system')
  })

  function setTheme(t: Theme) {
    theme.value = t
  }

  function toggle() {
    theme.value = theme.value === 'light' ? 'dark' : 'light'
  }

  return { theme, setTheme, toggle }
})
